"use client";

import { cn } from "@devhop/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { format, isToday } from "date-fns";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const WEEKDAY_MON_FIRST: readonly string[] = [
  "ຈ",
  "ອ",
  "ພ",
  "ພຫ",
  "ສຸ",
  "ເສ",
  "ອາ",
];

export type ShopShiftListFiltersProps = {
  branchId: string | undefined;
  branches: { id: string; name: string }[];
  onBranchChange: (branchId: string | undefined) => void;
  weekDays: Date[];
  weekLabel: string;
  weekOffset: number;
  onWeekOffsetChange: (offset: number | ((prev: number) => number)) => void;
  countsByDate: Map<string, number>;
  selectedDayKey: string | null;
  onSelectDay: (dateKey: string) => void;
  showEmptyDays: boolean;
  onShowEmptyDaysChange: (show: boolean) => void;
};

export function ShopShiftListFilters({
  branchId,
  branches,
  onBranchChange,
  weekDays,
  weekLabel,
  onWeekOffsetChange,
  countsByDate,
  selectedDayKey,
  onSelectDay,
  showEmptyDays,
  onShowEmptyDaysChange,
}: ShopShiftListFiltersProps) {
  return (
    <section className="mb-4 space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-[12rem] flex-1 lg:max-w-xs">
          <p className="mb-1.5 font-medium text-card-foreground text-xs">
            ສາຂາ
          </p>
          <SimpleSelect
            value={branchId ?? ""}
            onValueChange={(v) => onBranchChange(v || undefined)}
            placeholder="ເລືອກສາຂາ"
            options={branches.map((b) => ({
              value: b.id,
              label: b.name,
            }))}
            className="h-10 w-full"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex size-10 items-center justify-center rounded-lg border border-border hover:bg-muted/50 touch-manipulation"
            onClick={() => onWeekOffsetChange((w) => w - 1)}
            aria-label="ອາທິດກ່ອນ"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="min-w-[11rem] text-center font-semibold text-foreground text-sm tabular-nums">
            {weekLabel}
          </span>
          <button
            type="button"
            className="inline-flex size-10 items-center justify-center rounded-lg border border-border hover:bg-muted/50 touch-manipulation"
            onClick={() => onWeekOffsetChange((w) => w + 1)}
            aria-label="ອາທິດຖັດໄປ"
          >
            <ChevronRight className="size-4" />
          </button>
          <button
            type="button"
            className="h-10 rounded-lg border border-primary/30 bg-primary/10 px-3 font-medium text-primary text-sm hover:bg-primary/15 touch-manipulation"
            onClick={() => onWeekOffsetChange(0)}
          >
            ອາທິດນີ້
          </button>
        </div>
      </div>

      <div>
        <p className="mb-2 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
          ເລືອກວັນ
        </p>
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
          {weekDays.map((day, index) => {
            const key = format(day, "yyyy-MM-dd");
            const count = countsByDate.get(key) ?? 0;
            const today = isToday(day);
            const selected = selectedDayKey === key;
            const weekdayLabel = WEEKDAY_MON_FIRST[index] ?? "";

            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelectDay(key)}
                className={cn(
                  "flex flex-col items-center rounded-xl border px-1 py-2 transition-colors touch-manipulation sm:px-2 sm:py-2.5",
                  selected
                    ? "border-primary bg-primary/10 ring-2 ring-primary/25"
                    : today
                      ? "border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/15"
                      : "border-border bg-muted/20 hover:bg-muted/40",
                )}
              >
                <span className="font-medium text-[10px] text-muted-foreground uppercase">
                  {weekdayLabel}
                </span>
                <span
                  className={cn(
                    "mt-0.5 font-bold text-base tabular-nums leading-none",
                    today && !selected && "text-emerald-800 dark:text-emerald-200",
                  )}
                >
                  {format(day, "d")}
                </span>
                <span
                  className={cn(
                    "mt-1 rounded-full px-1.5 py-px font-semibold text-[10px] tabular-nums",
                    count > 0
                      ? "bg-foreground/10 text-foreground"
                      : "text-muted-foreground/70",
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 rounded border-border"
          checked={showEmptyDays}
          onChange={(e) => onShowEmptyDaysChange(e.target.checked)}
        />
        <span className="text-muted-foreground">
          ສະແດງວັນທີ່ບໍ່ມີກະ
        </span>
      </label>
    </section>
  );
}
